export type EpisodeSection = {
  id: string;
  title: string;
  paragraphs: string[];
};

export type EpisodeActor = {
  role: string;
  actor: string;
};

export type Episode = {
  id: string;
  title: string;
  subtitle: string;
  cover: string;
  summary: string;
  date: string;
  season: string;
  type: string;
  duration: string;
  episodeNo: number;
  tags: string[];
  actors: EpisodeActor[];
  sections: EpisodeSection[];
};

export const episodes: Episode[] = [
  {
    id: "ep-01",
    title: "第一章 · 雨夜归人",
    subtitle: "旧城重逢",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20a%20rainy%20old%20city%20alley%20at%20night%2C%20moody%20deep%20blue%20shadows%20with%20warm%20orange%20streetlamp%20glow%2C%20reflective%20wet%20pavement%2C%20dramatic%20lighting%2C%20artistic%20composition%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-01&orientation=landscape",
    summary:
      "暴雨之夜，阔别多年的程野拖着行李箱回到旧城，与母亲旧友苏晚晴在巷口意外重逢。一段被时间掩埋的往事，随着这场雨悄然浮现。",
    date: "2026-09-01",
    season: "第一季",
    type: "剧情介绍",
    duration: "48 分钟",
    episodeNo: 1,
    tags: ["家庭", "伦理", "悬疑"],
    actors: [
      { role: "苏晚晴", actor: "林知夏" },
      { role: "程野", actor: "顾承宇" },
      { role: "程母", actor: "周慧兰" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "故事从一场突如其来的暴雨开始。离家十年的程野因母亲的病讯被迫回到旧城，行李箱的滚轮碾过积水的青石板，声音在空荡的巷子里格外清晰。",
          "他在熟悉的巷口遇见了母亲当年的挚友苏晚晴。她撑着一把旧伞，站在便利店昏黄的灯下，仿佛已经等候多时。两个人都没有先开口。",
          "这场重逢并不温柔。旧城里每个人都记得那段往事，却没有人愿意完整地讲出来。",
        ],
      },
      {
        id: "opening",
        title: "开场铺垫",
        paragraphs: [
          "导演用近三分钟的无声镜头交代了旧城的空间：狭窄的巷道、斑驳的墙面、被雨水浸透的红色招牌。没有配乐，只有雨声。",
          "苏晚晴这一角色在第一集几乎没有正面特写，她始终以背影或侧影出现，为后续的悬念埋下伏笔。",
        ],
      },
      {
        id: "turning",
        title: "关键转折",
        paragraphs: [
          "当程野推开老屋的门，发现客厅里挂着一张自己从未见过的合影，照片上的三个人笑得灿烂，唯独缺了母亲。",
          "他终于开口问出第一句话：「这张照片，是什么时候拍的？」剧集在此戛然而止。",
        ],
      },
    ],
  },
  {
    id: "ep-02",
    title: "第二章 · 沉默的饭桌",
    subtitle: "一场迟到的晚餐",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20a%20dimly%20lit%20family%20dining%20table%20with%20three%20empty%20chairs%2C%20warm%20amber%20hanging%20lamp%20against%20deep%20blue%20shadowy%20room%2C%20melancholic%20mood%2C%20artistic%20lighting%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-02&orientation=landscape",
    summary:
      "程野与母亲共进十年来第一顿晚餐，桌上摆满了他儿时爱吃的菜，可两人却始终找不到话题。沉默比争吵更让人窒息。",
    date: "2026-09-05",
    season: "第一季",
    type: "剧情介绍",
    duration: "46 分钟",
    episodeNo: 2,
    tags: ["家庭", "温情", "剧情"],
    actors: [
      { role: "程野", actor: "顾承宇" },
      { role: "程母", actor: "周慧兰" },
      { role: "苏晚晴", actor: "林知夏" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "母亲做了一整桌菜，全是程野小时候最喜欢的。可十年过去，他的口味早就变了，却还是一口一口地吃完。",
          "饭桌上母亲几次欲言又止，最后只是问了一句：「那边……过得好吗？」程野没有回答，只是低头扒饭。",
        ],
      },
      {
        id: "detail",
        title: "细节铺陈",
        paragraphs: [
          "本集大量使用静止镜头捕捉母亲夹菜时微微发抖的手，暗示她的身体状况并不乐观。",
          "窗外邻居孩子的笑声反复出现，与屋内沉重的沉默形成强烈对比。",
        ],
      },
    ],
  },
  {
    id: "ep-03",
    title: "第三章 · 旧相册",
    subtitle: "被撕掉的那一页",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20an%20old%20photo%20album%20open%20on%20a%20wooden%20desk%2C%20one%20page%20torn%20out%2C%20warm%20desk%20lamp%20light%20contrasting%20deep%20blue%20dark%20background%2C%20mysterious%20mood%2C%20artistic%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-03&orientation=landscape",
    summary:
      "程野在整理旧物时翻出一本相册，发现其中一页被人刻意撕掉。邻居的一句话，让当年的秘密初现端倪。",
    date: "2026-09-12",
    season: "第一季",
    type: "剧情介绍",
    duration: "45 分钟",
    episodeNo: 3,
    tags: ["悬疑", "家庭", "剧情"],
    actors: [
      { role: "程野", actor: "顾承宇" },
      { role: "老邻居", actor: "赵国强" },
      { role: "苏晚晴", actor: "林知夏" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "程野在阁楼整理母亲旧物，翻出一本布满灰尘的相册。相册按年份排列，唯独 1998 年那一页被整齐地撕掉。",
          "他向老邻居打听当年的往事，老人只说了一句：「那年冬天，你妈搬走了一阵子。」",
        ],
      },
      {
        id: "clue",
        title: "线索展开",
        paragraphs: [
          "撕掉的相册页、母亲的搬离、苏晚晴刻意回避的眼神，三条线索在本集首次交汇。",
        ],
      },
    ],
  },
  {
    id: "ep-04",
    title: "第四章 · 邻家灯火",
    subtitle: "一墙之隔的温度",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20warm%20glowing%20windows%20of%20old%20apartment%20buildings%20at%20dusk%2C%20deep%20blue%20evening%20sky%2C%20cozy%20orange%20interior%20light%2C%20nostalgic%20emotional%20mood%2C%20artistic%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-04&orientation=landscape",
    summary:
      "程野开始留意隔壁苏晚晴的生活。两个陌生又熟悉的家庭，在同一个楼道里渐渐靠近，也在靠近中互相试探。",
    date: "2026-09-19",
    season: "第一季",
    type: "剧情介绍",
    duration: "47 分钟",
    episodeNo: 4,
    tags: ["家庭", "温情", "伦理"],
    actors: [
      { role: "苏晚晴", actor: "林知夏" },
      { role: "程野", actor: "顾承宇" },
      { role: "苏母", actor: "陈玉萍" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "程野发现苏晚晴一直独自照顾年迈的母亲，生活并不容易。他开始主动帮忙，从修灯泡到买菜，两个家庭的距离被一点点拉近。",
          "但在一次闲聊中，苏晚晴无意间提到「你母亲当年对我很好」，这句话让程野心里泛起涟漪。",
        ],
      },
      {
        id: "relation",
        title: "人物关系",
        paragraphs: [
          "本集正式确立程野与苏晚晴之间「似亲非亲、似友非友」的微妙关系，为后续情感线做铺垫。",
        ],
      },
    ],
  },
  {
    id: "ep-05",
    title: "第五章 · 暴雨再临",
    subtitle: "真相的前夜",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20heavy%20rain%20against%20a%20single%20lit%20window%20in%20a%20dark%20building%2C%20deep%20blue%20storm%20tones%20with%20warm%20orange%20window%20glow%2C%20tense%20dramatic%20mood%2C%20artistic%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-05&orientation=landscape",
    summary:
      "又一场暴雨落下，母亲的病情突然恶化，程野在医院走廊里等来了一直想知道的答案，却发现自己可能并不想听。",
    date: "2026-09-26",
    season: "第一季",
    type: "剧情介绍",
    duration: "49 分钟",
    episodeNo: 5,
    tags: ["悬疑", "家庭", "剧情"],
    actors: [
      { role: "程野", actor: "顾承宇" },
      { role: "程母", actor: "周慧兰" },
      { role: "苏晚晴", actor: "林知夏" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "母亲在雨夜病情恶化被送进医院，程野守在走廊的长椅上整整一夜。",
          "凌晨时分，苏晚晴赶来，终于说出了一部分隐藏多年的真相——那年冬天，程母离开旧城，是为了保护一个人。",
        ],
      },
      {
        id: "climax",
        title: "情绪高潮",
        paragraphs: [
          "本集是前半季的情绪高点。医院白炽灯下的长镜头与雨声交织，成为全季最具张力的段落。",
        ],
      },
    ],
  },
  {
    id: "ep-06",
    title: "第六章 · 一封信",
    subtitle: "迟到十年的解释",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20a%20handwritten%20letter%20held%20under%20warm%20light%2C%20deep%20blue%20dark%20surroundings%2C%20intimate%20emotional%20focus%2C%20shallow%20depth%20of%20field%2C%20artistic%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-06&orientation=landscape",
    summary:
      "程野在母亲的抽屉里发现一封从未寄出的信。信上的字迹颤抖而克制，一个被埋藏的决定终于有了完整的解释。",
    date: "2026-10-03",
    season: "第一季",
    type: "剧情介绍",
    duration: "44 分钟",
    episodeNo: 6,
    tags: ["家庭", "温情", "伦理"],
    actors: [
      { role: "程野", actor: "顾承宇" },
      { role: "程母", actor: "周慧兰" },
      { role: "苏晚晴", actor: "林知夏" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "程野在整理母亲的抽屉时，找到一封写于十年前、从未寄出的信。收信人正是他自己。",
          "信中没有辩解，只有一个母亲笨拙却真诚的坦白。程野在窗边读完整封信，天亮了。",
        ],
      },
      {
        id: "letter",
        title: "信件内容",
        paragraphs: [
          "信中母亲写道：「有些路我必须一个人走，不是不要你，是怕连累你。」",
          "这段独白由演员周慧兰配音完成，成为全季传播度最高的片段之一。",
        ],
      },
    ],
  },
  {
    id: "ep-07",
    title: "第七章 · 幕后手记",
    subtitle: "雨夜戏的诞生",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20behind%20the%20scenes%20film%20set%20at%20night%2C%20camera%20rig%20and%20rain%20machines%2C%20deep%20blue%20ambient%20light%20with%20warm%20orange%20practical%20lamps%2C%20documentary%20mood%2C%20artistic%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-07&orientation=landscape",
    summary:
      "本集为幕后花絮特辑：剧组如何用真实的雨、受限的空间与自然光，拍出前三章标志性的雨夜影像风格。",
    date: "2026-10-05",
    season: "第一季",
    type: "幕后花絮",
    duration: "30 分钟",
    episodeNo: 7,
    tags: ["幕后", "花絮", "制作"],
    actors: [
      { role: "导演", actor: "沈立川" },
      { role: "摄影指导", actor: "吴一航" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "特辑简介",
        paragraphs: [
          "这支花絮记录了第一集中那场三分钟无声雨戏的拍摄过程。为了保持真实质感，剧组拒绝了棚内造雨的方案，坚持在真实的巷子里等雨。",
        ],
      },
      {
        id: "method",
        title: "创作方法",
        paragraphs: [
          "摄影指导透露，全片的蓝色基调来自现场路灯与月光的自然混色，几乎没有使用后期调色。",
          "演员在雨中连续拍摄了六个通宵，很多镜头都是第一条。",
        ],
      },
    ],
  },
  {
    id: "ep-08",
    title: "第八章 · 空椅子",
    subtitle: "团圆之后",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20one%20empty%20wooden%20chair%20at%20a%20family%20table%20by%20a%20window%20at%20dawn%2C%20soft%20cool%20blue%20morning%20light%20with%20warm%20accent%2C%20poignant%20emotional%20mood%2C%20artistic%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-08&orientation=landscape",
    summary:
      "病情好转的母亲出院回家，一家人第一次真正坐在一起吃饭。但桌上永远空着的一把椅子，提醒着所有人缺失的部分。",
    date: "2026-10-08",
    season: "第一季",
    type: "剧情介绍",
    duration: "46 分钟",
    episodeNo: 8,
    tags: ["家庭", "温情", "剧情"],
    actors: [
      { role: "程野", actor: "顾承宇" },
      { role: "程母", actor: "周慧兰" },
      { role: "苏晚晴", actor: "林知夏" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "母亲病情好转出院，程野决定留下来。两人第一次像真正的母子那样坐在一起吃饭、聊天、拌嘴。",
          "但当程野问起「爸去哪里了」，母亲的笑容僵住了。桌上的第四把椅子，从头到尾都没有人坐。",
        ],
      },
      {
        id: "theme",
        title: "主题呈现",
        paragraphs: [
          "本集用「团圆」反衬「缺失」，把家庭叙事从和解推向更深的追问。",
        ],
      },
    ],
  },
  {
    id: "ep-09",
    title: "第九章 · 老照片里的第三个人",
    subtitle: "父亲的名字",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20a%20faded%20old%20family%20photograph%20with%20a%20mysterious%20figure%2C%20held%20in%20hands%2C%20deep%20blue%20dim%20lighting%20with%20warm%20orange%20edge%20glow%2C%20mystery%20mood%2C%20artistic%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-09&orientation=landscape",
    summary:
      "程野追查相册中被撕掉的那一页，终于拼出了第三个人的轮廓。这个名字，将旧城所有人的关系连成一张网。",
    date: "2026-10-11",
    season: "第一季",
    type: "剧情介绍",
    duration: "50 分钟",
    episodeNo: 9,
    tags: ["悬疑", "伦理", "剧情"],
    actors: [
      { role: "程野", actor: "顾承宇" },
      { role: "老邻居", actor: "赵国强" },
      { role: "苏晚晴", actor: "林知夏" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "程野从老邻居家找到一张未撕毁的同款照片复制件，照片上第三个人的面容终于清晰。",
          "这个人是旧城所有人都认识、却都刻意不提的存在。随着名字被说出，苏晚晴终于崩溃。",
        ],
      },
      {
        id: "reveal",
        title: "关键揭晓",
        paragraphs: [
          "本集揭晓了贯穿全季的核心秘密，也重新定义了程野与苏晚晴两家之间复杂的情感纽带。",
        ],
      },
    ],
  },
  {
    id: "ep-10",
    title: "第十章 · 天台上的选择",
    subtitle: "向前走",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20two%20people%20standing%20on%20a%20rooftop%20at%20sunset%20looking%20over%20an%20old%20city%2C%20deep%20blue%20dusk%20sky%20with%20warm%20orange%20horizon%2C%20hopeful%20dramatic%20mood%2C%20artistic%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-10&orientation=landscape",
    summary:
      "真相揭开之后，程野和苏晚晴站在天台上，面对各自的选择。旧城还在，人事已非，往后的路只能自己走。",
    date: "2026-10-13",
    season: "第一季",
    type: "剧情介绍",
    duration: "48 分钟",
    episodeNo: 10,
    tags: ["家庭", "剧情", "温情"],
    actors: [
      { role: "程野", actor: "顾承宇" },
      { role: "苏晚晴", actor: "林知夏" },
      { role: "程母", actor: "周慧兰" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "剧情梗概",
        paragraphs: [
          "真相并没有带来想象中的解脱。程野和苏晚晴在天台上第一次坦诚相对，谈起了各自的恐惧与遗憾。",
          "母亲在楼下喊他们吃饭，两人相视一笑，走下楼去。",
        ],
      },
      {
        id: "ending",
        title: "段落收束",
        paragraphs: [
          "本集为第一季的收束，用一次平凡的晚餐作为结尾，把悬念留给后续季。",
        ],
      },
    ],
  },
  {
    id: "ep-11",
    title: "第十一章 · 角色手记",
    subtitle: "苏晚晴的前半生",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20a%20woman%20silhouette%20by%20a%20window%20with%20curtains%2C%20deep%20blue%20shadows%20and%20warm%20orange%20light%20streak%2C%20introspective%20melancholic%20mood%2C%20artistic%20portrait%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-11&orientation=landscape",
    summary:
      "番外特别篇：从苏晚晴的视角重述整段往事，补完这位核心角色在主线之外沉默的十年。",
    date: "2026-10-14",
    season: "特别篇",
    type: "角色解读",
    duration: "35 分钟",
    episodeNo: 11,
    tags: ["角色", "解读", "剧情"],
    actors: [
      { role: "苏晚晴", actor: "林知夏" },
      { role: "苏母", actor: "陈玉萍" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "特别篇简介",
        paragraphs: [
          "本集完全从苏晚晴的视角展开，讲述她在主线剧情之外，独自承担的那部分故事。",
        ],
      },
      {
        id: "perspective",
        title: "视角补完",
        paragraphs: [
          "很多主线里看似冷漠的举动，在这一集里都有了解释。她的沉默不是疏离，而是更深的守护。",
        ],
      },
    ],
  },
  {
    id: "ep-12",
    title: "第十二章 · 城市的声音",
    subtitle: "影像之外",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20an%20empty%20old%20city%20street%20at%20blue%20hour%2C%20distant%20warm%20orange%20shop%20lights%2C%20quiet%20atmospheric%20mood%2C%20artistic%20wide%20composition%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=fm3-ep-12&orientation=landscape",
    summary:
      "制作特辑：配乐、环境音与旧城实景如何共同塑造出作品的沉浸氛围，一起走进影像之外的声音世界。",
    date: "2026-10-14",
    season: "特别篇",
    type: "幕后花絮",
    duration: "28 分钟",
    episodeNo: 12,
    tags: ["幕后", "配乐", "制作"],
    actors: [
      { role: "配乐", actor: "方声远" },
      { role: "声音设计", actor: "谭亦" },
    ],
    sections: [
      {
        id: "synopsis",
        title: "特辑简介",
        paragraphs: [
          "这一集把镜头对准声音团队。剧中大量使用真实环境音，从雨声、蝉鸣到旧城清晨的自行车铃，都来自实地采集。",
        ],
      },
      {
        id: "sound",
        title: "声音设计",
        paragraphs: [
          "配乐刻意保持克制，全季主题旋律只完整出现两次，一次在开场，一次在终章。",
        ],
      },
    ],
  },
];